/// <reference types="./types/broker-globals" />
import { lazy, Suspense, useEffect } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { BrandProvider } from "@/hooks/useBrand";
import { DomainGate } from "@/components/auth/DomainGate";
import { ImpersonationBar } from "@/components/layout/ImpersonationBar";
import { PageLoader } from "@/components/layout/PageLoader";
import { AppSettingsWarmup } from "@/components/layout/AppSettingsWarmup";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { isChunkLoadError, shouldAutoReload } from "@/lib/chunk-reload";
import { toast } from "sonner";
import { reportError } from '@/lib/error-tracking';
import { isMarketingDomain } from '@/lib/domain';
import { Analytics } from '@vercel/analytics/react';
import { MetaPixelProvider } from '@/components/tracking/MetaPixelProvider';

import { SoftphoneProvider } from '@/components/softphone/SoftphoneContext';
import { InstallPrompt } from '@/components/pwa/InstallPrompt';

// Lazy-loaded page components
// The signed-in half of the app — every guarded route and the five route guards
// themselves — behind a lazy boundary, so none of it lands in the entry chunk that a
// public visitor downloads. Reached below via <Route path="*">. New authenticated routes
// belong in that file, NOT here: adding one here re-imports the guard into the entry
// chunk and silently gives the bundle win back, with nothing going red (#3616).
const AuthenticatedApp = lazy(() => import("./AuthenticatedApp"));
// The `.catch` is load-bearing, not defensive habit. SoftphoneMount sits at app root
// as a SIBLING of ChunkErrorGuard, so there is no ErrorBoundary anywhere above it —
// every ancestor is a plain provider, and main.tsx's try/catch cannot see a throw from
// a later render pass. React's lazy() attaches its own handler to the import promise
// and re-throws during render, so useChunkErrorListener's `unhandledrejection` never
// fires either. Without this, a failed fetch of the softphone chunk (flaky wifi on
// first load after sign-in, a stale tab across a deploy) would unmount the root and
// hand a signed-in broker a blank page. Before this file was split the failure could
// not happen — the container was a static import.
//
// Degrading to `null` rather than routing into ErrorBoundary/shouldAutoReload is
// deliberate: the softphone is non-essential background chrome, and reloading a
// working page because an optional chunk failed is the same hazard that got the
// authenticated-shell prefetch dropped from this PR. The broker loses the dialer until
// their next reload — exactly as if they lacked the `phone` permission — instead of
// losing the app. Sentry still hears about it.
const SoftphoneContainer = lazy(() =>
  import("@/components/softphone/SoftphoneContainer")
    .then((m) => ({ default: m.SoftphoneContainer }))
    .catch((err) => {
      reportError(err, { component: 'SoftphoneContainer.lazy' });
      return { default: () => null };
    }),
);

const Home = lazy(() => import("./pages/Home"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Auth = lazy(() => import("./pages/Auth"));

const PublicListing = lazy(() => import("./pages/PublicListing"));
const Blog = lazy(() => import("./pages/Blog"));
const Contact = lazy(() => import("./pages/Contact"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SellerIntake = lazy(() => import("./pages/SellerIntake"));
const PublicSellerIntakeStart = lazy(() => import("./pages/PublicSellerIntakeStart"));
const SellerValueMagnet = lazy(() => import("./pages/SellerValueMagnet"));
const BuyerDataRoom = lazy(() => import("./pages/BuyerDataRoom"));
const BuyerPreferences = lazy(() => import("./pages/BuyerPreferences"));
const NDASigningPage = lazy(() => import("./pages/NDASigningPage"));
const MatchPassed = lazy(() => import("./pages/MatchPassed"));
// Buyer onboarding flow (#2134) — magic-link landing pad + wizard + reveal.
// Renders the #2135 holding stub itself while app_settings.
// buyer_onboarding_enabled is off (the default).
const BuyerOnboarding = lazy(() => import("./pages/BuyerOnboarding"));
const MyFrankly = lazy(() => import("./pages/MyFrankly"));
const BuyerSubscription = lazy(() => import("./pages/BuyerSubscription"));
const MatchExpired = lazy(() => import("./pages/MatchExpired"));
const MatchUnavailable = lazy(() => import("./pages/MatchUnavailable"));
const NDAVerify = lazy(() => import("./pages/NDAVerify"));

// Global safety net: catch chunk-load failures that escape React Suspense
// (e.g., a fire-and-forget dynamic import in a hook effect). Shares the
// TTL+counter state in src/lib/chunk-reload.ts with the ErrorBoundary so
// the two handlers cooperate on a single attempt budget instead of
// stomping on each other's sessionStorage flag (which used to produce
// the infinite "A new version is available" loop — follow-up to #1313).
function useChunkErrorListener() {
  useEffect(() => {
    const handler = (e: PromiseRejectionEvent) => {
      if (!isChunkLoadError(e.reason)) return;
      e.preventDefault();
      // shouldAutoReload bumps the shared counter and tells us whether
      // we're still under the cap. If we're at the cap, do nothing —
      // the next React render that touches a chunk will hit the
      // ErrorBoundary, which surfaces the persistent UI.
      if (shouldAutoReload()) {
        window.location.reload();
      }
    };
    window.addEventListener('unhandledrejection', handler);
    return () => window.removeEventListener('unhandledrejection', handler);
  }, []);
}


// App domain root: /auth signed out, /dashboard signed in. /dashboard itself
// is role-aware (#2530 — see src/pages/RoleDashboard.tsx): buyer-type
// sessions (the metadata stamp buyer-register writes, checked sync via
// src/lib/buyer-session.ts) render the My Frankly desk there; staff get the
// classic dashboard. Keeping the arbitration inside the route — not here —
// means every "go home" path (this redirect, Auth.tsx, module-gate bounces)
// inherits it for free. This supersedes #2529's /my-frankly redirect;
// /my-frankly stays a working alias of the desk.
function AppRoot() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  return <Navigate to={user ? "/dashboard" : "/auth"} replace />;
}

// Mounts the softphone at app root — outside <Routes>, exactly where it has always been,
// so a broker on a live call keeps their Twilio Device across every navigation including
// onto a public URL. Only the CODE moved: SoftphoneContainer statically pulled
// @twilio/voice-sdk (782 KB of source) and @radix-ui/react-select into the entry chunk for
// every anonymous visitor, and the `!user` gate below is what keeps that chunk unfetched
// for them. It duplicates the container's own first gate (SoftphoneContainer.tsx), so it
// changes when the chunk is fetched, never what renders.
function SoftphoneMount() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <Suspense fallback={null}>
      <SoftphoneContainer />
    </Suspense>
  );
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      reportError(error, { component: 'QueryCache', queryKey: String(query.queryKey) });
      // Only surface errors for queries that have already loaded data (background refetch failures).
      // Suppress transient network errors — they resolve on the next automatic refetch.
      const isNetworkError = error instanceof TypeError && /fetch|network/i.test(error.message);
      if (query.state.data !== undefined && !isNetworkError) {
        toast.error(`Something went wrong: ${error.message}`);
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      reportError(error, { component: 'MutationCache' });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
      networkMode: 'online', // Pause queries when browser is offline/resource-exhausted
    },
  },
});

// Strip the `?_cb=<ts>` cache-buster the ErrorBoundary's Refresh button
// appends. Without this, the param sticks in the URL forever and gets
// dragged along by every internal navigation that preserves query params
// (filter pages in /listings, /matches, /inbox, etc.).
function useStripCacheBusterParam() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (url.searchParams.has('_cb')) {
      url.searchParams.delete('_cb');
      window.history.replaceState(null, '', url.toString());
    }
  }, []);
}

function ChunkErrorGuard({ children }: { children: React.ReactNode }) {
  useChunkErrorListener();
  useStripCacheBusterParam();
  return <>{children}</>;
}

const App = () => (
  <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrandProvider>
          {/* Above DomainGate on purpose: that gate replaces the whole app with "Access Denied"
              when the signed-in user has no access to the current domain's brand, and an
              impersonated user often won't — leaving no way back without this (#2836). */}
          <ImpersonationBar />
          {/* Warms ['app-settings'] once per session (#3266 PR 7). ListingHubRoute
              routes on the deal-hub flag and reads an unresolved flag as "off",
              so a cold cache would cost a converted listing an extra hop with a
              frame of the listing hub in between. Renders nothing. */}
          <AppSettingsWarmup />
          <DomainGate>
          <TooltipProvider>
            <Analytics />
            <Toaster />
            <Sonner />
            {!isMarketingDomain() && <InstallPrompt />}
            <BrowserRouter>
              <MetaPixelProvider>
              <SoftphoneProvider>
              <ChunkErrorGuard>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Public routes */}
                  <Route path="*" element={
                    <ErrorBoundary fallbackTitle="Page Error">
                      <Routes>
                        {isMarketingDomain() ? (
                          <>
                            <Route path="/" element={<Home />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/terms" element={<Terms />} />
                            <Route path="/deal/:id" element={<PublicListing />} />
                            <Route path="/blog" element={<Blog />} />
                            <Route path="/blog/:slug" element={<BlogPost />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/seller-intake/start" element={<PublicSellerIntakeStart />} />
                            <Route path="/seller-intake/value" element={<SellerValueMagnet />} />
                            <Route path="/seller-intake/:token" element={<SellerIntake />} />
                            <Route path="/dataroom/:token" element={<BuyerDataRoom />} />
                            <Route path="/buyer/preferences/:token" element={<BuyerPreferences />} />
                            {/* Buyer onboarding flow (#2134). Flag-gated inside the pages:
                                buyer_onboarding_enabled off → holding stub / 404. */}
                            <Route path="/buyer/onboarding" element={<BuyerOnboarding />} />
                            <Route path="/buyer/subscription" element={<BuyerSubscription />} />
                            <Route path="/my-frankly" element={<MyFrankly />} />
                            <Route path="/my-frankly/join" element={<MyFrankly join />} />
                            <Route path="/match/passed" element={<MatchPassed />} />
                            <Route path="/match/expired" element={<MatchExpired />} />
                            <Route path="/match/unavailable" element={<MatchUnavailable />} />
                            <Route path="/nda/verify" element={<NDAVerify />} />
                            <Route path="/nda/:token" element={<NDASigningPage />} />
                          </>
                        ) : (
                          <>
                            <Route path="/" element={<AppRoot />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/terms" element={<Terms />} />
                            <Route path="/deal/:id" element={<PublicListing />} />
                            <Route path="/seller-intake/start" element={<PublicSellerIntakeStart />} />
                            <Route path="/seller-intake/value" element={<SellerValueMagnet />} />
                            <Route path="/seller-intake/:token" element={<SellerIntake />} />
                            <Route path="/dataroom/:token" element={<BuyerDataRoom />} />
                            <Route path="/buyer/preferences/:token" element={<BuyerPreferences />} />
                            {/* Buyer onboarding flow (#2134). Flag-gated inside the pages:
                                buyer_onboarding_enabled off → holding stub / 404. */}
                            <Route path="/buyer/onboarding" element={<BuyerOnboarding />} />
                            <Route path="/buyer/subscription" element={<BuyerSubscription />} />
                            <Route path="/my-frankly" element={<MyFrankly />} />
                            <Route path="/my-frankly/join" element={<MyFrankly join />} />
                            <Route path="/match/passed" element={<MatchPassed />} />
                            <Route path="/match/expired" element={<MatchExpired />} />
                            <Route path="/match/unavailable" element={<MatchUnavailable />} />
                            <Route path="/nda/verify" element={<NDAVerify />} />
                            <Route path="/nda/:token" element={<NDASigningPage />} />
                          </>
                        )}

                        {/* Auth */}
                        <Route path="/auth" element={<Auth />} />
                        <Route path="/reset-password" element={<ResetPassword />} />

                        {/* Everything else — the signed-in app, its guards and the 404 —
                            lives in the lazily-loaded AuthenticatedApp so a public visitor
                            never downloads it (#3616). Ranking, not order, decides: React
                            Router puts `*` below every concrete path above, so each public
                            route still wins. The descendant <Routes> inside still matches
                            absolute paths because a root-level splat leaves pathnameBase
                            at `/` — the same reason the wrapper two levels up works. */}
                        <Route path="*" element={<AuthenticatedApp />} />
                      </Routes>
                    </ErrorBoundary>
                  } />
                </Routes>
              </Suspense>
              </ChunkErrorGuard>
              <SoftphoneMount />
              </SoftphoneProvider>
              </MetaPixelProvider>
            </BrowserRouter>
          </TooltipProvider>
          </DomainGate>
          </BrandProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </HelmetProvider>
);

export default App;
